import { supabase } from './supabase';

/**
 * Calls the embed-title Edge Function to compute a 1536-dim OpenAI embedding
 * for a lot title. Used by SalesCompStrip + SalesCompsModal to enable the
 * Phase 1b semantic-match tier in find_sales_comps / list_sales_comps.
 *
 * Cached in-memory per title so repeated views of the same lot don't re-call
 * OpenAI. The cache is module-scoped (process-lifetime) and unbounded — titles
 * are small strings and the entry count stays low in normal use.
 */

const cache = new Map<string, number[]>();
const pending = new Map<string, Promise<number[] | null>>();

export async function embedTitle(title: string): Promise<number[] | null> {
  const text = (title ?? '').trim();
  if (text.length < 3) return null;

  const cached = cache.get(text);
  if (cached) return cached;

  const inflight = pending.get(text);
  if (inflight) return inflight;

  type EmbedResponse = {
    ok: boolean;
    embedding?: number[];
    reason?: string;
    error?: string;
  };

  const p = (async (): Promise<number[] | null> => {
    try {
      const resp = await supabase.functions.invoke<EmbedResponse>('embed-title', {
        body: { text },
      });
      if (resp.error) throw resp.error;
      const body: EmbedResponse | null = resp.data ?? null;
      if (!body?.ok || !Array.isArray(body.embedding)) {
        console.warn('embed-title returned non-OK response', body);
        return null;
      }
      const vec: number[] = body.embedding;
      cache.set(text, vec);
      return vec;
    } catch (err) {
      console.warn('embed-title failed:', err);
      return null;
    } finally {
      pending.delete(text);
    }
  })();

  pending.set(text, p);
  return p;
}
